import { Card } from "@/components/Card";
import { Button } from "@/components/generic/Button";
import { Heading } from "@/components/generic/Heading";
import {
  IntegrationSetupWizard,
  loadIntegrationSetupData,
} from "@/components/integrations";
import { SettingsContainer } from "@/components/layout/SettingsContainer";
import {
  useAvailableIntegrationsQuery,
  useConnectIntegrationMutation,
  useIntegrationConnectionInfoMutation,
  useIntegrationsQuery,
} from "@/queries/integrations";
import { Integration, ProviderConnectionInfo } from "@/types/integrations";
import { createFileRoute } from "@tanstack/react-router";
import { Check } from "lucide-react";
import { useMemo, useState } from "react";
import { toast } from "sonner";

type IntegrationState =
  | { type: "list" }
  | { type: "setup"; integration: string }
  | { type: "configure"; integration: string };

export const Route = createFileRoute(
  "/_dashboard/campaigns/setup/delivery/integration/select/new",
)({
  component: RouteComponent,
  beforeLoad: () => {
    console.debug("Loading integration selection route...");
  },
  loader: () => ({
    showCampaignSetupSubStepper: false,
    showCampaignSetupBottomBar: false,
  }),
});

function RouteComponent() {
  const { user, queryClient } = Route.useRouteContext();
  const navigate = Route.useNavigate();
  const [state, setState] = useState<IntegrationState>({ type: "list" });
  const [selectedIntegration, setSelectedIntegration] = useState<string | null>(
    null,
  );

  // Fetch available and connected integrations
  const { data: availableIntegrations = [] } = useAvailableIntegrationsQuery();
  const { data: connectedIntegrations = [] } = useIntegrationsQuery();

  const { mutateAsync } = useConnectIntegrationMutation();

  // Pre-load setup data when an integration is selected for setup
  const [setupData, setSetupData] = useState<Awaited<
    ReturnType<typeof loadIntegrationSetupData>
  > | null>(null);

  const connectedIntegrationNames = useMemo(() => {
    return new Set(
      connectedIntegrations.map(
        (i: ProviderConnectionInfo) => i.provider?.toLowerCase() ?? "",
      ),
    );
  }, [connectedIntegrations]);

  // Filter out storage-only integrations for CRM/delivery purposes
  const crmIntegrations = useMemo(() => {
    return availableIntegrations.filter(
      (i: Integration) =>
        !connectedIntegrations.some(
          (cI) => cI.provider.toLowerCase() === i.uniqueName.toLowerCase(),
        ) && ["CRM", "Dialer"].includes(i.category), // TODO - include CustomAPI when possible to set up in Sales
    );
  }, [availableIntegrations, connectedIntegrations]);

  const handleSelectIntegration = async (integrationName: string) => {
    const isConnected = connectedIntegrationNames.has(
      integrationName.toLowerCase(),
    );

    if (isConnected) {
      // For already connected integrations, just select them
      setSelectedIntegration(integrationName.toLowerCase());
    } else {
      // For new integrations, load setup data and start wizard
      const data = await loadIntegrationSetupData(
        queryClient,
        integrationName.toLowerCase(),
        "setup",
      );
      setSetupData(data);
      setState({ type: "setup", integration: integrationName.toLowerCase() });
    }
  };

  const handleSetupComplete = async (
    connectionInfo: ProviderConnectionInfo,
  ) => {
    const mutatePromise = mutateAsync({
      data: connectionInfo,
      integration: connectionInfo.provider.toLowerCase(),
    });
    toast.promise(mutatePromise, {
      loading: "Opretter integration...",
      success: "Integration oprettet!",
      error: "Der skete en fejl ved oprettelse af integration.",
    });
    await mutatePromise;
    // Integration is now connected, select it for the campaign
    setSelectedIntegration(connectionInfo.provider?.toLowerCase() ?? null);
    setState({ type: "list" });
    setSetupData(null);
    navigate({
      to: "/campaigns/setup/delivery/integration/select",
      search: (prev) => ({
        ...prev,
        integration: connectionInfo.provider?.toLowerCase(),
      }),
    });
  };

  const handleSetupCancel = () => {
    setState({ type: "list" });
    setSetupData(null);
    navigate({
      to: "/campaigns/setup/delivery/integration/select",
    });
  };

  // Render wizard for new integration setup
  if (state.type === "setup" && setupData) {
    return (
      <SettingsContainer
        border
        backLinkOptions={{
          to: "/campaigns/setup/delivery/integration/select",
        }}
      >
        <div className="mx-auto flex h-full w-full flex-col">
          <IntegrationSetupWizard
            integration={state.integration}
            mode="setup"
            onComplete={handleSetupComplete}
            onCancel={handleSetupCancel}
            capabilities={setupData.capabilities}
            configMetadata={setupData.configMetadata}
            hasSettings={setupData.hasSettings}
            user={user}
            successAutoNavigateDelay={1500}
          />
        </div>
      </SettingsContainer>
    );
  }

  // Render integration list
  return (
    <SettingsContainer
      border
      backLinkOptions={{
        to: "/campaigns/setup/delivery/integration/select",
      }}
    >
      <div className="flex flex-col gap-y-8">
        <div>
          <Heading level={2} className="mb-2">
            Vælg det system du ønsker at integrere med
          </Heading>
        </div>

        {/* Integration grid */}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {crmIntegrations.map((integration) => {
            const isConnected = connectedIntegrationNames.has(
              integration.uniqueName.toLowerCase(),
            );
            const isSelected =
              selectedIntegration === integration.uniqueName.toLowerCase();

            return (
              <Card
                key={integration.uniqueName}
                theme="border"
                className={`flex cursor-pointer items-center gap-x-4 p-4 transition-all hover:shadow-md ${
                  isSelected ? "ring-theme-primary ring-2" : ""
                }`}
                onClick={() => handleSelectIntegration(integration.uniqueName)}
              >
                {integration.iconSVG && (
                  <div
                    className="shrink-0 *:size-10"
                    dangerouslySetInnerHTML={{ __html: integration.iconSVG }}
                  />
                )}
                <div className="flex-1">
                  <div className="flex items-center gap-x-2">
                    <span className="font-medium">{integration.name}</span>
                    {isConnected && (
                      <span className="text-xs text-green-600 dark:text-green-400">
                        Forbundet
                      </span>
                    )}
                  </div>
                  {integration.description && (
                    <p className="text-theme-on-background-variant text-xs">
                      {integration.description}
                    </p>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      </div>
    </SettingsContainer>
  );
}
